import { useId } from 'react'
import Image from 'next/image'
import { IconCheckDefault } from '@mc/design-system'
import { cloudflareLoader } from '@mc/image-cloudflare'
import { CohortStateBlock } from '../hero/CohortStateBlock'
import { getStateCopy } from '../hero/getStateCopy'
import type {
  CohortConfig,
  CohortContent,
  EnrollmentStateResult,
} from '../types'

type CohortClosingCtaProps = {
  config: CohortConfig
  content: CohortContent
  enrollment: EnrollmentStateResult
  onNotify?: () => void
}

export const CohortClosingCta = ({
  config,
  content,
  enrollment,
  onNotify,
}: CohortClosingCtaProps) => {
  const headingId = useId()
  const { closingCta } = content

  return (
    <section aria-labelledby={headingId} className='py-8 lg:container'>
      <div className='flex flex-col overflow-hidden rounded-xl bg-c-beige-1300 text-c-text-light lg:flex-row'>
        <div className='relative aspect-square lg:aspect-auto lg:w-5/12'>
          <Image
            src={closingCta.image.url}
            alt={closingCta.image.alt}
            fill
            loader={cloudflareLoader}
            sizes='(min-width: 960px) 42vw, 100vw'
            className='object-cover'
          />
        </div>

        <div className='flex flex-col gap-6 p-6 lg:flex-1 lg:p-7'>
          <h2 id={headingId} className='c-d-1 m-0 text-center lg:text-left'>
            {closingCta.headline}
          </h2>
          <ul className='m-0 flex list-none flex-col gap-4 p-0'>
            {closingCta.bullets.map((bullet) => (
              <li key={bullet} className='c-body flex items-start gap-3'>
                <IconCheckDefault
                  size='sm'
                  className='mt-1 shrink-0 text-c-brand-highlight'
                />
                {bullet}
              </li>
            ))}
          </ul>
          <hr className='m-0 border-0 border-t border-dotted border-c-base-900' />
          <CohortStateBlock
            copy={getStateCopy(content, config, enrollment)}
            joinLabel={closingCta.ctaLabel}
            onNotify={onNotify}
            align='between'
          />
        </div>
      </div>
    </section>
  )
}
